export const GOOGLE_GMAIL_SEND_SCOPE = "https://www.googleapis.com/auth/gmail.send";
export const GOOGLE_DRIVE_FILE_SCOPE = "https://www.googleapis.com/auth/drive.file";
export const GOOGLE_SPREADSHEETS_SCOPE = "https://www.googleapis.com/auth/spreadsheets";
export const GOOGLE_KEEP_SCOPE = "https://www.googleapis.com/auth/keep";
export const GOOGLE_CALENDAR_EVENTS_OWNED_READONLY_SCOPE =
  "https://www.googleapis.com/auth/calendar.events.owned.readonly";
const GOOGLE_BUNDLE_BASE_REQUIRED_SCOPES = [
  GOOGLE_DRIVE_FILE_SCOPE,
];
const GOOGLE_CALENDAR_TODAY_READ_REQUIRED_SCOPES = [
  GOOGLE_CALENDAR_EVENTS_OWNED_READONLY_SCOPE,
];
export const GOOGLE_RECEIPT_SCOPE_BUNDLE = "receipt_base";
export const GOOGLE_NEWS_SCOPE_BUNDLE = "news_gmail_send";
export const GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE = "bundle_base";
export const GOOGLE_CALENDAR_TODAY_READ_SCOPE_BUNDLE = "calendar_today_read";

export type GoogleConnectResolutionValidationParams = {
  expectedScopeBundle: string;
  resolvedScopeBundle?: string;
  resolvedScopes?: string[];
  authorizationUrl?: string;
  allowGmailSendScopeInReceipt?: boolean;
};

export type GoogleConnectResolutionValidationReason =
  | "ok"
  | "missing_resolved_fields"
  | "scope_bundle_mismatch"
  | "forbidden_scope_in_receipt"
  | "missing_required_scope_in_news"
  | "forbidden_scope_in_news"
  | "missing_required_scope_in_bundle"
  | "missing_required_scope_in_calendar_today_read";

export type GoogleConnectResolutionValidationResult = {
  isValid: boolean;
  reason: GoogleConnectResolutionValidationReason;
  expectedScopeBundle: string;
  resolvedScopeBundle?: string;
  hasResolvedPayload: boolean;
  containsGmailSendScope: boolean;
};

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeScopes(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => normalize(item))
    .filter((item): item is string => Boolean(item));
}

function mergeUniqueScopes(primary: string[], secondary: string[]): string[] {
  const merged = [...primary];
  for (const scope of secondary) {
    if (!merged.includes(scope)) {
      merged.push(scope);
    }
  }
  return merged;
}

function containsGmailSendScope(scopes: string[]) {
  return scopes.some((scope) => scope === GOOGLE_GMAIL_SEND_SCOPE);
}

function containsReceiptScopes(scopes: string[]) {
  return scopes.some(
    (scope) => scope === GOOGLE_DRIVE_FILE_SCOPE || scope === GOOGLE_SPREADSHEETS_SCOPE
  );
}

function containsBundleBaseScopes(scopes: string[]) {
  return scopes.some((scope) => scope === GOOGLE_DRIVE_FILE_SCOPE);
}

function containsCalendarTodayReadScopes(scopes: string[]) {
  return scopes.some((scope) => scope === GOOGLE_CALENDAR_EVENTS_OWNED_READONLY_SCOPE);
}

function parseScopesFromAuthorizationUrl(value: unknown): string[] {
  const url = normalize(value);
  if (!url) return [];

  try {
    const parsed = new URL(url);
    const rawScope = parsed.searchParams.get("scope");
    if (!rawScope) return [];

    return rawScope
      .replace(/\+/g, " ")
      .split(/[\s,]+/)
      .map((scope) => normalize(scope))
      .filter((scope): scope is string => Boolean(scope));
  } catch {
    return [];
  }
}

function shouldDisableGrantedScopeMerging(expectedScopeBundle: string): boolean {
  return (
    expectedScopeBundle === GOOGLE_RECEIPT_SCOPE_BUNDLE ||
    expectedScopeBundle === GOOGLE_NEWS_SCOPE_BUNDLE ||
    expectedScopeBundle === GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE ||
    expectedScopeBundle === GOOGLE_CALENDAR_TODAY_READ_SCOPE_BUNDLE
  );
}

export function normalizeGoogleAuthorizationUrlForScope(
  authorizationUrl: string,
  expectedScopeBundle: string,
  additionalRequiredScopes: string[] = []
): string {
  const url = normalize(authorizationUrl);
  if (!url) return authorizationUrl;

  try {
    const parsed = new URL(url);
    const isGoogleAuthOrigin = parsed.origin === "https://accounts.google.com";
    const isGoogleAuthPath =
      parsed.pathname === "/o/oauth2/auth" || parsed.pathname === "/o/oauth2/v2/auth";
    const hasScopeParam = parsed.searchParams.has("scope");

    if (!isGoogleAuthOrigin || !isGoogleAuthPath || !hasScopeParam) {
      return authorizationUrl;
    }

    if (!shouldDisableGrantedScopeMerging(expectedScopeBundle)) {
      return authorizationUrl;
    }

    parsed.searchParams.set("include_granted_scopes", "false");

    const scopeParam = parsed.searchParams.get("scope");
    if (scopeParam) {
      const rawScopes = scopeParam
        .replace(/\+/g, " ")
        .split(/[\s,]+/)
        .map((scope) => normalize(scope))
        .filter((scope): scope is string => Boolean(scope));

      const requiredScopes =
        expectedScopeBundle === GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE
          ? GOOGLE_BUNDLE_BASE_REQUIRED_SCOPES
          : expectedScopeBundle === GOOGLE_CALENDAR_TODAY_READ_SCOPE_BUNDLE
            ? GOOGLE_CALENDAR_TODAY_READ_REQUIRED_SCOPES
          : normalizeScopes(additionalRequiredScopes);

      if (requiredScopes.length > 0) {
        const mergedScopes = mergeUniqueScopes(rawScopes, requiredScopes);
        parsed.searchParams.set("scope", mergedScopes.join(" "));
      }
    }

    return parsed.toString();
  } catch {
    return authorizationUrl;
  }
}

export function validateGoogleConnectResolution(
  params: GoogleConnectResolutionValidationParams
): GoogleConnectResolutionValidationResult {
  const expectedScopeBundle = normalize(params.expectedScopeBundle) ?? "";
  const resolvedScopeBundle = normalize(params.resolvedScopeBundle);
  const resolvedScopes = normalizeScopes(params.resolvedScopes);
  const authorizationUrlScopes = parseScopesFromAuthorizationUrl(params.authorizationUrl);
  const hasResolvedPayload = resolvedScopeBundle !== undefined && Array.isArray(params.resolvedScopes);
  const effectiveScopes = hasResolvedPayload
    ? mergeUniqueScopes(resolvedScopes, authorizationUrlScopes)
    : authorizationUrlScopes;
  const hasGmailSendScope = containsGmailSendScope(effectiveScopes);
  const hasReceiptScopes = containsReceiptScopes(effectiveScopes);

  if (!hasResolvedPayload) {
    if (expectedScopeBundle === GOOGLE_NEWS_SCOPE_BUNDLE) {
      if (!hasGmailSendScope) {
        return {
          isValid: false,
          reason: "missing_required_scope_in_news",
          expectedScopeBundle,
          resolvedScopeBundle,
          hasResolvedPayload: false,
          containsGmailSendScope: false,
        };
      }

      if (hasReceiptScopes) {
        return {
          isValid: false,
          reason: "forbidden_scope_in_news",
          expectedScopeBundle,
          resolvedScopeBundle,
          hasResolvedPayload: false,
          containsGmailSendScope: true,
        };
      }
    }

    if (
      expectedScopeBundle === GOOGLE_CALENDAR_TODAY_READ_SCOPE_BUNDLE &&
      !containsCalendarTodayReadScopes(effectiveScopes)
    ) {
      return {
        isValid: false,
        reason: "missing_required_scope_in_calendar_today_read",
        expectedScopeBundle,
        resolvedScopeBundle,
        hasResolvedPayload: false,
        containsGmailSendScope: hasGmailSendScope,
      };
    }

    return {
      isValid: true,
      reason: "missing_resolved_fields",
      expectedScopeBundle,
      resolvedScopeBundle,
      hasResolvedPayload: false,
      containsGmailSendScope: hasGmailSendScope,
    };
  }

  if (resolvedScopeBundle !== expectedScopeBundle) {
    return {
      isValid: false,
      reason: "scope_bundle_mismatch",
      expectedScopeBundle,
      resolvedScopeBundle,
      hasResolvedPayload: true,
      containsGmailSendScope: hasGmailSendScope,
    };
  }

  if (
    expectedScopeBundle === GOOGLE_RECEIPT_SCOPE_BUNDLE &&
    hasGmailSendScope &&
    !params.allowGmailSendScopeInReceipt
  ) {
    return {
      isValid: false,
      reason: "forbidden_scope_in_receipt",
      expectedScopeBundle,
      resolvedScopeBundle,
      hasResolvedPayload: true,
      containsGmailSendScope: true,
    };
  }

  if (expectedScopeBundle === GOOGLE_NEWS_SCOPE_BUNDLE) {
    if (!hasGmailSendScope) {
      return {
        isValid: false,
        reason: "missing_required_scope_in_news",
        expectedScopeBundle,
        resolvedScopeBundle,
        hasResolvedPayload: true,
        containsGmailSendScope: false,
      };
    }

    if (hasReceiptScopes) {
      return {
        isValid: false,
        reason: "forbidden_scope_in_news",
        expectedScopeBundle,
        resolvedScopeBundle,
        hasResolvedPayload: true,
        containsGmailSendScope: true,
      };
    }
  }

  if (expectedScopeBundle === GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE) {
    if (!containsBundleBaseScopes(effectiveScopes)) {
      return {
        isValid: false,
        reason: "missing_required_scope_in_bundle",
        expectedScopeBundle,
        resolvedScopeBundle,
        hasResolvedPayload: true,
        containsGmailSendScope: hasGmailSendScope,
      };
    }
  }

  if (expectedScopeBundle === GOOGLE_CALENDAR_TODAY_READ_SCOPE_BUNDLE) {
    if (!containsCalendarTodayReadScopes(effectiveScopes)) {
      return {
        isValid: false,
        reason: "missing_required_scope_in_calendar_today_read",
        expectedScopeBundle,
        resolvedScopeBundle,
        hasResolvedPayload: true,
        containsGmailSendScope: hasGmailSendScope,
      };
    }
  }

  return {
    isValid: true,
    reason: "ok",
    expectedScopeBundle,
    resolvedScopeBundle,
    hasResolvedPayload: true,
    containsGmailSendScope: hasGmailSendScope,
  };
}
